@file:Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")

package com.wintermar.inspection.feature.domain.service

import com.wintermar.inspection.core.domain.FlowBatchResult
import com.wintermar.inspection.core.ui.component.image.util.FileChunkModel
import com.wintermar.inspection.core.ui.component.image.util.streamChunkFiles
import com.wintermar.inspection.feature.domain.usecase.answer.UploadChunkFileUseCase
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.asFlow
import kotlinx.coroutines.launch
import org.koin.core.component.KoinComponent
import org.koin.core.component.inject
import kotlin.js.js


actual class ChunkUploadService : KoinComponent {

    private val uploadChunkFileUseCase: UploadChunkFileUseCase by inject()

    // JS is single-threaded, Dispatchers.Default is fine
    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())
    private val queue = Channel<UploadTask>(Channel.UNLIMITED)
    private val activeJobs = mutableMapOf<String, Job>()

    // Registry so reattach() can swap the callback while the worker is mid-flight
    private val liveCallbacks = mutableMapOf<String, Pair<String, ChunkUploadCallback>>()

    init {
        registerServiceWorker()
        startWorker()
    }

    private fun startWorker() {
        scope.launch {
            for (task in queue) {
                // Sequential: process each file one by one
                task.files.forEach { fileModel ->
                    val job = launch {
                        try {
                            val (questionId, callback) = liveCallbacks[fileModel.id]
                                ?: return@launch

                            // Notify the UI that the setup phase for this specific file is done
                            callback.onDoneGenerateChunk(questionId, listOf(fileModel.id))

                            // Prepare the streaming pipeline dynamically.
                            // If chunks are already cached, use them; otherwise, pipe the stream directly.
                            val chunksFlow = if (fileModel.chunkFiles.isNotEmpty() || fileModel.hasGenerateChunk) {
                                fileModel.chunkFiles.asFlow()
                            } else {
                                streamChunkFiles(fileModel, targetSizeKb = 250)
                            }

                            // Pipe the clean, memory-safe stream directly into the UseCase
                            uploadChunkFileUseCase(chunksFlow).collect { result ->
                                val (currentQuestionId, currentCallback) = liveCallbacks[fileModel.id]
                                    ?: return@collect

                                when (result) {
                                    is FlowBatchResult.Success -> {
                                        result.data?.let { currentCallback.onProgress(currentQuestionId, it) }
                                    }

                                    is FlowBatchResult.Completed -> {
                                        currentCallback.onCompleted(currentQuestionId, fileModel.id, result.data)
                                    }

                                    is FlowBatchResult.Error -> {
                                        currentCallback.onError(currentQuestionId, fileModel.id, result.error)
                                    }

                                    else -> {}
                                }
                            }
                        } catch (e: CancellationException) {
                            throw e
                        } catch (e: Exception) {
                            liveCallbacks[fileModel.id]?.let { (questionId, callback) ->
                                callback.onError(questionId, fileModel.id, e)
                            }
                        } finally {
                            activeJobs.remove(fileModel.id)
                            liveCallbacks.remove(fileModel.id)
                        }
                    }
                    activeJobs[fileModel.id] = job
                    job.join() // Wait for this file to finish completely before moving to the next one
                }
            }
        }
    }

    actual fun enqueue(task: UploadTask) {
        task.files.forEach { file ->
            liveCallbacks[file.id] = Pair(task.questionId, task.callback)
        }
        scope.launch { queue.send(task) }
    }

    actual fun reattach(questionId: String, fileId: String, callback: ChunkUploadCallback) {
        if (activeJobs[fileId]?.isActive == true) {
            liveCallbacks[fileId] = Pair(questionId, callback)
        }
    }

    actual fun cancel(fileId: String?) {
        if (fileId != null) {
            activeJobs[fileId]?.cancel()
            activeJobs.remove(fileId)
            liveCallbacks.remove(fileId)
        } else {
            activeJobs.values.forEach { it.cancel() }
            activeJobs.clear()
            liveCallbacks.clear()
            queue.cancel()
            startWorker()
        }
    }

    private fun registerServiceWorker() {
        js(
            """
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.register('/service-worker.js', { scope: '/' })
                    .then(function(reg) { console.log('[SW] Registered:', reg.scope); })
                    .catch(function(err) { console.warn('[SW] Registration failed:', err); });
            }
        """
        )
    }
}
